import { useState } from 'react'
import { motion } from 'motion/react'
import { Check, Download, ArrowRight, Music, Globe, Calendar, MessageSquare, Smartphone, Search } from 'lucide-react'
import { useSEO } from '@/hooks/useSEO'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Button, Card, CardContent } from '@/components/ui'
import { useScrollReveal } from '@gjl/motion/css'

const CHECKLIST_ITEMS = [
  {
    icon: Music,
    title: 'A clear program description',
    description: "Who you are, what you do, who it's for. A visitor should understand your program in 10 seconds.",
  },
  {
    icon: Globe,
    title: 'Online applications or audition sign-ups',
    description: 'Not a PDF. Not "email us." A real form that collects what you need and confirms automatically.',
  },
  {
    icon: Calendar,
    title: 'An events page with RSVP or tickets',
    description: 'With dates, times, locations, and a way to act. Updated for this season, not last.',
  },
  {
    icon: MessageSquare,
    title: 'A real way to contact you',
    description: 'A contact form, not a mailto link. Phone number and address visible. Map if you have a venue.',
  },
  {
    icon: Smartphone,
    title: 'Mobile-friendly design',
    description: "Over half your audience is on their phone. If your site doesn't work on mobile, it doesn't work.",
  },
]

const BONUS = {
  icon: Search,
  title: 'Bonus: Does your site show up on Google?',
  description: "Search your program's name. If you're not in the first few results, you have an SEO problem.",
}

function openAuditEmail() {
  // eslint-disable-next-line no-restricted-syntax -- external redirect (mailto: opens the mail client), not SPA navigation
  window.location.href = "mailto:garrett@fuguelab.com?subject=Free Website Audit&body=Here's my website URL: "
}

export default function ChecklistPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')

  const breadcrumbs = generateBreadcrumbs('/checklist', breadcrumbLabels)
  const heroRef = useScrollReveal<HTMLDivElement>()
  const listRef = useScrollReveal<HTMLDivElement>()
  const formRef = useScrollReveal<HTMLDivElement>()
  const bonusRef = useScrollReveal<HTMLDivElement>()

  useSEO({
    title: '5 Things Every Music Program Website Needs | Free Checklist',
    description:
      "Download the free checklist: 5 things every music program's website needs to attract students, sell tickets, and grow your audience.",
    url: '/checklist',
    canonical: '/checklist',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '5 Things Every Music Program Website Needs',
      description: "Free checklist for music directors to evaluate their program's online presence.",
      mainEntity: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        url: 'https://fuguelab.com',
      },
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setIsSubmitting(true)
    setError('')

    try {
      // For now, trigger PDF download directly
      // TODO: Connect to Mailchimp when API key is configured
      const link = document.createElement('a')
      link.href = '/checklist.pdf'
      link.download = 'fugue-lab-website-checklist.pdf'
      link.click()

      setIsSubmitted(true)
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-16 px-4 py-16">
      {/* Hero */}
      <motion.div
        ref={heroRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6 text-center"
      >
        <div className="bg-primary/10 text-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
          <Download className="h-4 w-4" />
          Free Download
        </div>
        <h1 className="text-fluid-h1 text-foreground leading-tight font-bold">
          5 Things Every Music Program's Website Needs
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-xl">
          Most music program websites are missing at least two of these. Find out where yours stands — and what to fix
          first.
        </p>
      </motion.div>

      {/* Checklist Preview */}
      <motion.div
        ref={listRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-4"
      >
        {CHECKLIST_ITEMS.map((item, i) => (
          <Card key={i} className="border-border/50 bg-card/50">
            <CardContent className="flex items-start gap-4 p-5">
              <div className="bg-primary/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
                <item.icon className="text-primary h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground font-mono text-sm">{i + 1}/5</span>
                  <h3 className="text-foreground font-semibold">{item.title}</h3>
                </div>
                <p className="text-muted-foreground mt-1">{item.description}</p>
              </div>
              <Check className="text-primary/30 mt-1 h-5 w-5 flex-shrink-0" />
            </CardContent>
          </Card>
        ))}

        {/* Bonus */}
        <motion.div
          ref={bonusRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="flex items-start gap-4 p-5">
              <div className="bg-primary/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
                <BONUS.icon className="text-primary h-5 w-5" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold">{BONUS.title}</h3>
                <p className="text-muted-foreground mt-1">{BONUS.description}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* Email Capture Form */}
      <motion.div
        ref={formRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="border-primary/20 bg-card">
          <CardContent className="space-y-6 p-8">
            {isSubmitted ? (
              <div className="space-y-4 py-4 text-center">
                <div className="bg-primary/10 mx-auto flex h-16 w-16 items-center justify-center rounded-full">
                  <Check className="text-primary h-8 w-8" />
                </div>
                <h3 className="text-foreground text-2xl font-bold">Check your downloads!</h3>
                <p className="text-muted-foreground">
                  Your checklist is downloading now. Want personalized feedback on your program's website?
                </p>
                <Button variant="outline" size="lg" onClick={openAuditEmail}>
                  Get a Free 5-Minute Audit
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2 text-center">
                  <h2 className="text-foreground text-2xl font-bold">Download the full checklist</h2>
                  <p className="text-muted-foreground">Get the printable PDF with scoring rubric and action steps.</p>
                </div>

                <form onSubmit={handleSubmit} className="mx-auto max-w-md space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="First name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-primary/50 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:ring-primary/50 w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
                    />
                  </div>
                  {error && <p className="text-sm text-red-500">{error}</p>}
                  <Button type="submit" size="lg" className="w-full" disabled={isSubmitting || !email}>
                    {isSubmitting ? (
                      <>
                        <span className="mr-2 size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Download Free Checklist
                      </>
                    )}
                  </Button>
                  <p className="text-muted-foreground text-center text-xs">
                    No spam. Just the checklist and occasional tips for music programs.
                  </p>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Audit CTA */}
      {!isSubmitted && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="space-y-4 pb-8 text-center"
        >
          <p className="text-muted-foreground">Want personalized feedback instead?</p>
          <Button variant="ghost" onClick={openAuditEmail}>
            Get a free 5-minute video audit of your site
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </motion.div>
      )}
    </div>
  )
}
